import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import Image from 'next/image';
import { type ReactElement, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Breadcrumb } from '@/components/Breadcrumb';
import type { CardProps } from '@/components/Cms/Card/types';
import type { ContentGroupProps } from '@/components/Cms/ContentGroup';
import { FixedPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { type ContentfulFixedPageCMSData } from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { distributors } from '@/lib/distributors';
import { paths } from '@/lib/paths';
import { useRegionProvider } from '@/providers/RegionProvider';

import { Contact } from './components/Contact';
import { OfficialDistributor } from './components/OfficialDistributor';
import { SendEmail } from './components/SendEmail';

import heroImage from '@/assets/contact-1920.png';

import * as S from './ContactUs.styles';

interface ContactUsViewProps {
  cms?: ContentfulFixedPageCMSData;
}

type Distributor = {
  address: string;
  city: string;
  displayName: string;
  fax: string;
  name: string;
  phone: string;
};
type Distributors = {
  [key: string]: Distributor;
};
export type SelectOption = {
  label: string;
  value: string;
};

export const ContactUs = ({ cms }: ContactUsViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const [distributorInfo, setDistributorInfo] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    fax: '',
  });
  const [defaultCountry, setDefaultCountry] = useState<SelectOption | null>(
    null,
  );

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { country } = useRegionProvider();

  const breadcrumbItems = [
    {
      label: formatMessage({ id: 'common.home', defaultMessage: 'Home' }),
      target: paths.home.url(),
    },
    {
      label: formatMessage({
        id: 'service.contact',
        defaultMessage: 'Contact Us',
      }),
      target: router.asPath,
    },
  ];

  useEffect(() => {
    autoSelectDistributor();
  }, [country]);
  const findDistributorKey = (
    countryName: string,
    distributors: Distributors,
  ): string | undefined => {
    return Object.keys(distributors).find(
      (key) => distributors[key].displayName === countryName,
    );
  };

  const getFallbackCountryKey = (): string => 'United_States';

  const autoSelectDistributor = () => {
    const countryKey =
      findDistributorKey(country.name, distributors) || getFallbackCountryKey();
    const distributor = (distributors as Distributors)[countryKey];
    const formattedCountryKey = countryKey.replace(/_/g, ' ');
    setDefaultCountry({ label: formattedCountryKey, value: countryKey });
    setDistributorInfo(distributor);
  };

  const handleDistributorChange = (selectedOption: SelectOption) => {
    const selectedDistributor = (distributors as Distributors)[
      selectedOption.value
    ];
    setDistributorInfo(selectedDistributor);
  };

  const distributorOptions: SelectOption[] = Object.keys(distributors).map(
    (countryKey) => ({
      label: (distributors as Distributors)[countryKey].displayName,
      value: countryKey,
    }),
  );

  const itemsArray = ((liveCms?.bottomContent?.[0] as ContentGroupProps)
    ?.itemsCollection?.items || []) as CardProps[];

  const getItemById = (id: string) => itemsArray.find((item) => item.id === id);

  const handleCallBreitlingClick = (number: string) => {
    const phoneNumber = number === '877-BREITLING' ? '+1 203-456-2458' : number;
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleEmailClick = (mail: string) => {
    window.location.href = `mailto:${mail}`;
  };
  return (
    <S.Container>
      <S.BreadcrumbWrapper>
        <Breadcrumb.Wrapper>
          <Breadcrumb items={breadcrumbItems} />
        </Breadcrumb.Wrapper>
      </S.BreadcrumbWrapper>
      <S.ImageWrapper>
        <Image
          alt='shopping bag icon'
          data-test-id='ConfirmationViewImage'
          fill
          src={
            (liveCms?.topContent?.[0] as CardProps)?.media?.image?.url ??
            heroImage
          }
          style={{ objectFit: 'cover', top: 0, objectPosition: 'top' }}
        />
      </S.ImageWrapper>
      <S.CardsWrapper>
        <Contact
          bodyText={getItemById('contact')?.cardText}
          handleCallBreitlingClick={handleCallBreitlingClick}
          header={getItemById('contact')?.placeholderHeading?.text}
          subheading={getItemById('contact')?.subHeading}
        />
        <OfficialDistributor
          defaultCountry={defaultCountry}
          distributorInfo={distributorInfo}
          distributorOptions={distributorOptions}
          handleCallBreitlingClick={handleCallBreitlingClick}
          handleDistributorChange={handleDistributorChange}
          header={getItemById('distributor')?.placeholderHeading?.text}
        />
        <SendEmail
          handleEmailClick={handleEmailClick}
          header={getItemById('email')?.placeholderHeading?.text}
        />
      </S.CardsWrapper>
    </S.Container>
  );
};
ContactUs.getLayout = (page: ReactElement) => (
  <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>
);
